import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useToggleSignalAgent } from "@/hooks/use-signal-agents";
import { useUsers } from "@/hooks/use-users";
import { hasMinRole } from "@/lib/constants";
import { formatNOK, formatPct } from "@/lib/format";
import { FREQUENCY_LABELS } from "@/lib/monitoring-sources";
import { costMonthKey, dismissShare, isCostCapReached, isLowValueAgent } from "@/lib/signal-agent-calc";
import { signalSourceLabel } from "@/lib/signal-types";
import type { DBSignalAgent } from "@/lib/db-types";

const SCOPE_LABELS: Record<DBSignalAgent["scope_type"], string> = {
  monitored: "Alle overvåkede",
  customers: "Utvalgte kunder",
  segment: "Eksternt segment",
};

interface Props {
  agents: DBSignalAgent[];
  hits: Map<string, number>;
  defaultCapNok: number;
  onEdit: (agent: DBSignalAgent) => void;
}

/** Tabellen over agenter. Redigering krever å eie agenten (egne) eller være admin (alle, også systemagenter). */
export default function SignalAgentsTable({ agents, hits, defaultCapNok, onEdit }: Props) {
  const { activeUser } = useImpersonation();
  const { data: users = [] } = useUsers();
  const toggle = useToggleSignalAgent();
  const isAdmin = hasMinRole(activeUser?.role ?? "external", "admin");
  const now = new Date();
  const owners = new Map(users.map((u) => [u.id, u.full_name]));
  const canEdit = (a: DBSignalAgent) => isAdmin || (!a.is_system && a.owner_id === activeUser?.id);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Agent</TableHead>
          <TableHead>Eier</TableHead>
          <TableHead>Utvalg</TableHead>
          <TableHead>Kilder</TableHead>
          <TableHead>Frekvens</TableHead>
          <TableHead className="text-right">Treff 30 d</TableHead>
          <TableHead className="text-right">Avvist</TableHead>
          <TableHead className="text-right">Kostnad i måneden</TableHead>
          <TableHead>Aktiv</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {agents.map((a) => {
          const share = dismissShare(a);
          const capReached = a.is_active && isCostCapReached(a, defaultCapNok, now);
          const spent = a.cost_month === costMonthKey(now) ? a.cost_month_nok : 0;
          return (
            <TableRow key={a.id}>
              <TableCell>
                <div className="font-medium">{a.name}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {a.is_system && <Badge variant="outline">System</Badge>}
                  {isLowValueAgent(a) && <StatusBadge tone="near" label="Lite nyttig" />}
                  {capReached && <StatusBadge tone="miss" label="Pauset: kostnadstak nådd" />}
                </div>
              </TableCell>
              <TableCell>{a.is_system ? "Revehiet" : owners.get(a.owner_id ?? "") ?? "—"}</TableCell>
              <TableCell>{SCOPE_LABELS[a.scope_type]}{a.scope_type === "customers" ? ` (${a.customer_ids?.length ?? 0})` : ""}</TableCell>
              <TableCell>{a.sources.length === 0 ? "Intern regel" : a.sources.map(signalSourceLabel).join(", ")}</TableCell>
              <TableCell>{FREQUENCY_LABELS[a.frequency]}</TableCell>
              <TableCell className="text-right">{hits.get(a.id) ?? 0}</TableCell>
              <TableCell className="text-right">{share == null ? "—" : formatPct(share * 100, 0)}</TableCell>
              <TableCell className="text-right">{formatNOK(spent)}</TableCell>
              <TableCell>
                <Switch
                  checked={a.is_active}
                  disabled={!canEdit(a) || toggle.isPending}
                  aria-label={`${a.is_active ? "Pause" : "Aktiver"} ${a.name}`}
                  onCheckedChange={(isActive) => toggle.mutate({ id: a.id, isActive })}
                />
              </TableCell>
              <TableCell className="text-right">
                {canEdit(a) && (
                  <Button size="sm" variant="ghost" aria-label={`Rediger ${a.name}`} onClick={() => onEdit(a)}>
                    Rediger
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
